import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/components/ui/ThemeProvider';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner, toast } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

/**
 * The backstop for anything that fails without telling the user.
 *
 * `useClient` already routes HTTP failures through `onError` into the error dialogs,
 * but that only covers requests. A client-side throw inside an async handler - a URL
 * that will not parse, a missing field, a bad cast - became an unhandled rejection:
 * a line in the console and, on screen, a button that appeared not to have worked.
 * Creating a website with "example.com" did exactly that.
 *
 * Individual handlers should still catch their own errors and say something specific;
 * this exists so that the failure mode when they do not is a visible toast rather
 * than silence.
 *
 * Request failures are skipped, because `onError` has already shown a dialog for
 * them and two reports of one problem is worse than one. They are recognisable by
 * the numeric `status` that `validateResponse` throws.
 */
const isHandledRequestError = (reason: unknown) =>
  typeof reason === 'object' && reason !== null && typeof (reason as { status?: unknown }).status === 'number';

const reportUnhandled = (reason: unknown) => {
  if (isHandledRequestError(reason)) return;
  const message = reason instanceof Error ? reason.message : 'Something went wrong.';
  toast.error(message);
};

window.addEventListener('unhandledrejection', (ev) => reportUnhandled(ev.reason));
window.addEventListener('error', (ev) => reportUnhandled(ev.error));

const elem = document.getElementById('root')!;
const app = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="pertento-theme">
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <App />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>
);

if (import.meta.hot) {
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  createRoot(elem).render(app);
}
